/**
 * The language a logged-out visitor asked for on the public share landing,
 * carried across register / login (LANGUAGE-REQUESTS.md §9).
 *
 * Stored as `localStorage['ee.pendingLanguageRequest'] = {code, ref, at}`.
 * `code` is kept only when it is a catalog code, `ref` only when it has the
 * share-code shape (S8). Kept until the server has it or refuses it for good
 * (the runner clears it), so a failed replay is retried, never lost.
 */
export const PENDING_LANGUAGE_KEY = 'ee.pendingLanguageRequest';
export const PENDING_LANGUAGE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const CODE_RE = /^[a-z]{2}$/;
const REF_RE = /^[A-Z2-9]{10}$/;

export interface PendingLanguageRequest {
  code: string;
  ref: string | null;
  at: number;
}

export function isLanguageCode(code: string | null | undefined): code is string {
  return typeof code === 'string' && CODE_RE.test(code);
}

export function stashPendingLanguageRequest(
  code: string,
  ref: string | null,
  catalogCodes: readonly string[],
  now: number = Date.now(),
): boolean {
  if (!isLanguageCode(code) || !catalogCodes.includes(code)) return false;
  const value: PendingLanguageRequest = {
    code,
    ref: ref && REF_RE.test(ref) ? ref : null,
    at: now,
  };
  try {
    localStorage.setItem(PENDING_LANGUAGE_KEY, JSON.stringify(value));
    return true;
  } catch {
    return false; // storage blocked: the visitor can still add it on the page
  }
}

export function clearPendingLanguageRequest(): void {
  try {
    localStorage.removeItem(PENDING_LANGUAGE_KEY);
  } catch {
    // storage blocked: nothing was stored either
  }
}

function parse(raw: string, now: number): PendingLanguageRequest | null {
  try {
    const parsed = JSON.parse(raw) as Partial<PendingLanguageRequest>;
    if (!isLanguageCode(parsed.code) || typeof parsed.at !== 'number') return null;
    if (now - parsed.at > PENDING_LANGUAGE_MAX_AGE_MS || parsed.at > now + 60_000) return null;
    const ref = typeof parsed.ref === 'string' && REF_RE.test(parsed.ref) ? parsed.ref : null;
    return { code: parsed.code, ref, at: parsed.at };
  } catch {
    return null;
  }
}

/**
 * The pending request, left in place. A malformed one or one older than 7
 * days is dropped and reads as null.
 */
export function peekPendingLanguageRequest(
  now: number = Date.now(),
): PendingLanguageRequest | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(PENDING_LANGUAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  const pending = parse(raw, now);
  if (!pending) clearPendingLanguageRequest();
  return pending;
}

/** Read the pending request and clear it. Null when absent, malformed or older than 7 days. */
export function takePendingLanguageRequest(
  now: number = Date.now(),
): PendingLanguageRequest | null {
  const pending = peekPendingLanguageRequest(now);
  clearPendingLanguageRequest();
  return pending;
}
